import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShoppingBag, Share2, Copy, Minus, Plus, Download, Heart, CheckCircle2, ChevronLeft,
  Truck, ShieldCheck, RotateCcw, Loader2, PackageX,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useCart, apiErrorMessage } from '@/contexts/CartContext';
import { useWishlist } from '@/contexts/WishlistContext';
import { cn, formatPrice } from '@/lib/utils';
import { useCartLine } from '@/hooks/useCartLine';
import CartStepper from '@/components/CartStepper';
import { PRODUCT_DETAIL } from '@/constants/testIds';
import { useSettings } from '@/contexts/SettingsContext';
import ProductImage from '@/components/ProductImage';
import { useVariantSelection } from '@/hooks/useVariantSelection';
import RecommendedSection from '@/components/RecommendedSection';
import ProductReviews from '@/components/ProductReviews';
import { addRecentlyViewed, getRecentlyViewed } from '@/lib/recentlyViewed';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);


const DetailSkeleton = () => (
  <div className="py-8">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
      <Skeleton className="h-4 w-48 mb-6" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
        <Skeleton className="w-full aspect-[4/5] rounded-3xl" />
        <div className="space-y-5">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-9 w-40" />
          <Skeleton className="h-40 w-full rounded-2xl" />
          <Skeleton className="h-14 w-full rounded-full" />
        </div>
      </div>
    </div>
  </div>
);

const ProductDetail = () => {
  const site = useSettings();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addToCart, findCartItem, openCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const [product, setProduct] = useState(null);
  const [adding, setAdding] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  const buildShareUrl = () => `${window.location.origin}/products/${id}`;

  useEffect(() => {
    setLoading(true);
    setQuantity(1);
    setSelectedImageIndex(0);
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data?.data || null);
      if (response.data?.data) addRecentlyViewed(response.data.data.id);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      setProduct(null);
      if (error?.response?.status !== 404) toast.error('فشل تحميل المنتج');
    } finally {
      setLoading(false);
    }
  };

  // افتراضياً: أول لون
  // ?variant=ID يفتح المنتج على تركيبة معينة (من المفضلة أو السلة)
  const { options, selected, selectedVariant, selectedLabel, selectValue: pickValue, images } = useVariantSelection(
    product,
    searchParams.get('variant')
  );

  // تغيير اللون بيغيّر الصور المعروضة — نرجع لأول صورة
  useEffect(() => {
    setSelectedImageIndex(0);
  }, [images]);

  const selectValue = (attributeId, valueId) => {
    pickValue(attributeId, valueId);
    setQuantity(1);
  };

  const displayPrice = selectedVariant?.price ?? product?.price;
  const originalPrice = product?.original_price > displayPrice ? product.original_price : null;
  const discount = originalPrice ? Math.round((1 - displayPrice / originalPrice) * 100) : null;

  // حالة التركيبة المختارة: موجودة في السلة؟ محفوظة في المفضلة؟
  const cartItem = findCartItem(selectedVariant?.id);
  const cartLine = useCartLine(selectedVariant?.id);
  const liked = isInWishlist(selectedVariant?.id);

  const handleWishlist = async () => {
    if (!selectedVariant) {
      toast.error('الرجاء اختيار اللون أولاً');
      return;
    }
    try {
      const added = await toggleWishlist(product, selectedVariant.id, selectedLabel);
      toast.success(added ? 'تمت الإضافة إلى المفضلة' : 'تمت الإزالة من المفضلة', { description: selectedLabel });
    } catch (error) {
      toast.error('تعذر تحديث المفضلة');
    }
  };

  const handleAddToCart = async () => {
    const missing = options.find((option) => !selected[option.attribute_id]);
    if (missing) {
      toast.error(`⚠️ الرجاء اختيار ${missing.name} قبل الإضافة إلى السلة`);
      return false;
    }

    if (!selectedVariant) {
      toast.error('الرجاء اختيار اللون أولاً');
      return false;
    }

    setAdding(true);
    try {
      await addToCart(selectedVariant.id, quantity);
      toast.success(`تمت إضافة ${quantity} × ${product.name_ar} إلى السلة`, {
        description: selectedLabel,
        action: { label: 'عرض السلة', onClick: openCart },
      });
      return true;
    } catch (error) {
      toast.error(apiErrorMessage(error, 'فشلت إضافة المنتج'));
      return false;
    } finally {
      setAdding(false);
    }
  };

  const handleBuyNow = async () => {
    // لو نفس الاختيار موجود في السلة بالفعل نروح للدفع مباشرة
    if (cartItem) {
      navigate('/checkout');
      return;
    }
    const success = await handleAddToCart();
    if (success) navigate('/checkout');
  };

  const handleShare = async () => {
    const shareUrl = buildShareUrl();
    if (navigator.share) {
      try {
        await navigator.share({ title: product.name_ar, text: `${product.name_ar} - ${formatPrice(displayPrice)}`, url: shareUrl });
      } catch (err) {
        // المستخدم قفل نافذة المشاركة
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('تم نسخ رابط المنتج');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(buildShareUrl());
    toast.success('تم نسخ الرابط');
  };

  const handleDownloadImage = async () => {
    const currentImgUrl = images[selectedImageIndex]?.url || images[0]?.url;
    if (!currentImgUrl) {
      toast.error('لا توجد صورة للتنزيل');
      return;
    }
    try {
      const response = await fetch(currentImgUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${product.name_ar.replace(/\s+/g, '_')}_image_${selectedImageIndex + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('تم تنزيل صورة المنتج بنجاح');
    } catch (err) {
      const a = document.createElement('a');
      a.href = currentImgUrl;
      a.download = `${product.name_ar}_image.jpg`;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('تم فتح تنزيل صورة المنتج');
    }
  };

  if (loading) return <DetailSkeleton />;

  if (!product) {
    return (
      <div className="py-24 text-center">
        <PackageX className="w-14 h-14 text-gray-300 mx-auto mb-4" />
        <p className="font-cairo text-lg text-gray-500 mb-6">المنتج غير موجود أو غير متاح حالياً</p>
        <Link to="/products">
          <Button className="bg-burgundy-500 hover:bg-burgundy-600 rounded-full px-8 font-cairo">تصفحي المنتجات</Button>
        </Link>
      </div>
    );
  }

  const whatsappShareText = `✨ شوفي هذا المنتج الرائع من ${site.site_name} ✨\n\n${product.name_ar}${selectedLabel ? `\n🎨 ${selectedLabel}` : ''}\n💰 السعر: ${formatPrice(displayPrice)}\n\n🔗 ${buildShareUrl()}`;
  const whatsappInquiryText = `مرحباً، أريد الاستفسار عن ${product.name_ar}${selectedLabel ? ` (${selectedLabel})` : ''}`;

  return (
    <div className="py-6 md:py-10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* مسار التنقل */}
        <nav aria-label="مسار التنقل" className="flex items-center gap-1.5 text-sm font-cairo text-gray-500 mb-6 overflow-x-auto whitespace-nowrap no-scrollbar">
          <Link to="/" className="hover:text-burgundy-500 transition-colors">الرئيسية</Link>
          <ChevronLeft className="w-4 h-4 shrink-0" />
          <Link to="/products" className="hover:text-burgundy-500 transition-colors">المنتجات</Link>
          {product.category && (
            <>
              <ChevronLeft className="w-4 h-4 shrink-0" />
              <Link to={`/products?category=${product.category.id}`} className="hover:text-burgundy-500 transition-colors">
                {product.category.name}
              </Link>
            </>
          )}
          <ChevronLeft className="w-4 h-4 shrink-0" />
          <span className="text-brand-black font-semibold truncate">{product.name_ar}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
          {/* الصور */}
          <div className="lg:sticky lg:top-32 self-start">
            <div data-testid={PRODUCT_DETAIL.image} className="relative rounded-3xl overflow-hidden bg-gray-100 border border-gray-200/70">
              <ProductImage
                src={images[selectedImageIndex]?.url || images[0]?.url}
                name={product.name_ar}
                className="w-full aspect-[4/5] object-contain"
                textClassName="text-2xl md:text-3xl"
              />
              {discount > 0 && (
                <span className="absolute top-4 right-4 bg-destructive text-white text-sm font-bold px-3 py-1 rounded-full shadow">
                  -{discount}%
                </span>
              )}
              <button
                type="button"
                onClick={handleWishlist}
                aria-label={liked ? 'إزالة من المفضلة' : 'إضافة إلى المفضلة'}
                aria-pressed={liked}
                className={cn(
                  'absolute top-4 left-4 w-11 h-11 rounded-full flex items-center justify-center bg-white/90 backdrop-blur shadow-md transition-all hover:scale-110',
                  liked ? 'text-destructive' : 'text-gray-600 hover:text-destructive'
                )}
              >
                <Heart className={cn('w-5 h-5', liked && 'fill-current')} />
              </button>
              {images.length > 1 && (
                <span
                  data-testid="product-image-count"
                  className="absolute bottom-4 left-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-cairo"
                >
                  {selectedImageIndex + 1} / {images.length}
                </span>
              )}
            </div>

            {images.length > 1 && (
              <div data-testid="product-thumbnails" className="flex gap-3 mt-4 overflow-x-auto no-scrollbar pb-1">
                {images.map((img, idx) => (
                  <button
                    key={img.id ?? idx}
                    type="button"
                    data-testid={`product-thumbnail-${idx}`}
                    onClick={() => setSelectedImageIndex(idx)}
                    aria-label={`صورة ${idx + 1}`}
                    className={cn(
                      'shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden bg-gray-100 border-2 transition-all',
                      selectedImageIndex === idx ? 'border-burgundy-500 shadow-md' : 'border-transparent opacity-70 hover:opacity-100'
                    )}
                  >
                    <img src={img.url} alt={`${product.name_ar} ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* التفاصيل */}
          <div className="flex flex-col gap-6">
            <div>
              {product.category && (
                <span className="inline-block text-xs font-cairo font-semibold text-burgundy-500 bg-burgundy-500/10 px-3 py-1 rounded-full mb-3">
                  {product.category.name}
                </span>
              )}
              <h1 data-testid={PRODUCT_DETAIL.name} className="text-3xl md:text-4xl font-tajawal font-bold text-brand-black leading-tight">
                {product.name_ar}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <p data-testid={PRODUCT_DETAIL.price} className="text-3xl md:text-4xl font-bold text-burgundy-500" aria-live="polite">
                  {formatPrice(displayPrice)}
                </p>
                {originalPrice && <p className="text-lg text-gray-400 line-through">{formatPrice(originalPrice)}</p>}
                {discount > 0 && (
                  <span className="text-xs font-bold text-destructive bg-destructive/10 px-2.5 py-1 rounded-full">وفّري {discount}%</span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-3">
                {selectedLabel && <span className="text-sm font-cairo text-gray-500">{selectedLabel}</span>}
              </div>
            </div>

            {/* الخصائص */}
            {options.length > 0 && (
              <div className="rounded-2xl border border-gray-200/80 bg-white p-4 md:p-5 space-y-5">
                {options.map((option) => {
                  const chosenId = selected[option.attribute_id];
                  const chosen = option.values.find((value) => value.id === chosenId);
                  return (
                    <div key={option.attribute_id} data-testid={`option-${option.attribute_id}`}>
                      <p className="font-tajawal font-semibold text-brand-black mb-2.5">
                        {option.name}:{' '}
                        <span className="text-burgundy-500 font-bold">{chosen?.value}</span>
                      </p>
                      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={option.name}>
                        {option.values.map((value) => {
                          const isSelected = chosenId === value.id;
                          return (
                            <button
                              key={value.id}
                              type="button"
                              role="radio"
                              aria-checked={isSelected}
                              onClick={() => selectValue(option.attribute_id, value.id)}
                              title={value.value}
                              className={cn(
                                'inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 font-cairo text-sm font-semibold transition-all',
                                isSelected
                                  ? 'border-burgundy-500 bg-burgundy-500 text-white shadow-md'
                                  : 'border-gray-200 text-gray-700 hover:border-burgundy-500 hover:text-burgundy-500'
                              )}
                            >
                              {option.is_color && value.hex && (
                                <span
                                  className={cn('w-4 h-4 rounded-full ring-1', isSelected ? 'ring-white/70' : 'ring-black/15')}
                                  style={{ backgroundColor: value.hex }}
                                />
                              )}
                              {value.value}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* حالة الاختيار الحالي: في السلة / في المفضلة */}
            {(cartItem || liked) && (
              <div key={selectedVariant?.id} className="flex flex-col gap-2 animate-in fade-in slide-in-from-top-1 duration-300">
                {cartItem && (
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                    <p className="flex items-center gap-2 font-cairo text-sm font-semibold text-emerald-800">
                      <CheckCircle2 className="w-5 h-5 shrink-0" />
                      هذا الاختيار موجود في سلتك (الكمية: {cartItem.quantity})
                    </p>
                    <button
                      type="button"
                      onClick={openCart}
                      className="shrink-0 text-sm font-cairo font-bold text-emerald-700 hover:text-emerald-900 underline-offset-4 hover:underline"
                    >
                      عرض السلة
                    </button>
                  </div>
                )}
                {liked && (
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
                    <p className="flex items-center gap-2 font-cairo text-sm font-semibold text-rose-800">
                      <Heart className="w-5 h-5 shrink-0 fill-current" />
                      هذا الاختيار محفوظ في المفضلة
                    </p>
                    <Link to="/wishlist" className="shrink-0 text-sm font-cairo font-bold text-rose-700 hover:text-rose-900 underline-offset-4 hover:underline">
                      عرض المفضلة
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* الكمية والأزرار */}
            <div className="space-y-3">
              <div className="flex items-stretch gap-3">
                {cartLine.inCart ? (
                  <>
                    <CartStepper
                      size="lg"
                      className="shrink-0 w-[128px] sm:w-[168px]"
                      quantity={cartLine.quantity}
                      busy={cartLine.busy}
                      canIncrement={cartLine.canIncrement}
                      onIncrement={cartLine.increment}
                      onDecrement={cartLine.decrement}
                    />
                    <Button
                      data-testid={PRODUCT_DETAIL.addToCartBtn}
                      onClick={openCart}
                      variant="outline"
                      className="flex-1 min-w-0 h-14 px-3 rounded-full border-2 border-burgundy-500 text-burgundy-500 hover:bg-burgundy-500 hover:text-white text-base md:text-lg font-cairo font-bold gap-2"
                    >
                      <ShoppingBag className="w-5 h-5" />
                      عرض السلة
                    </Button>
                  </>
                ) : (
                  <>
                    <div
                      className="inline-flex items-center rounded-full border-2 border-gray-200 bg-white"
                      data-testid="quantity-selector"
                      aria-label="الكمية"
                    >
                      <button
                        data-testid="quantity-increase"
                        type="button"
                        onClick={() => setQuantity(Math.min(99, quantity + 1))}
                        disabled={quantity >= 99}
                        aria-label="زيادة الكمية"
                        className="w-11 h-full flex items-center justify-center text-gray-600 hover:text-burgundy-500 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <span data-testid="quantity-value" className="w-8 text-center font-bold text-lg">{quantity}</span>
                      <button
                        data-testid="quantity-decrease"
                        type="button"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        disabled={quantity <= 1}
                        aria-label="تقليل الكمية"
                        className="w-11 h-full flex items-center justify-center text-gray-600 hover:text-burgundy-500 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    </div>

                    <Button
                      data-testid={PRODUCT_DETAIL.addToCartBtn}
                      onClick={handleAddToCart}
                      disabled={adding || !selectedVariant}
                      className="flex-1 h-14 rounded-full bg-burgundy-500 hover:bg-burgundy-600 text-base md:text-lg font-cairo gap-2 shadow-lg shadow-burgundy-500/20"
                    >
                      {adding ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShoppingBag className="w-5 h-5" />}
                      إضافة إلى السلة
                    </Button>
                  </>
                )}

                <button
                  type="button"
                  onClick={handleWishlist}
                  aria-label={liked ? 'إزالة من المفضلة' : 'إضافة إلى المفضلة'}
                  aria-pressed={liked}
                  className={cn(
                    'shrink-0 w-14 h-14 rounded-full border-2 flex items-center justify-center transition-all hover:scale-105',
                    liked ? 'border-destructive bg-destructive/5 text-destructive' : 'border-gray-200 text-gray-600 hover:border-destructive hover:text-destructive'
                  )}
                >
                  <Heart className={cn('w-5 h-5', liked && 'fill-current')} />
                </button>
              </div>

              <Button
                data-testid={PRODUCT_DETAIL.buyNowBtn}
                onClick={handleBuyNow}
                disabled={adding || !selectedVariant}
                variant="outline"
                className="w-full h-12 rounded-full border-2 border-burgundy-500 text-burgundy-500 hover:bg-burgundy-500 hover:text-white text-base font-cairo font-bold"
              >
                {cartItem ? 'إتمام الطلب' : 'اشتري الآن'}
              </Button>
            </div>

            {/* مميزات */}
            <div className="grid grid-cols-3 gap-2 rounded-2xl bg-gray-50 border border-gray-100 p-3">
              {[
                { icon: Truck, label: 'توصيل لجميع المناطق' },
                { icon: ShieldCheck, label: 'جودة مضمونة' },
                { icon: RotateCcw, label: 'سياسة إرجاع واضحة', to: '/return-policy' },
              ].map(({ icon: Icon, label, to }) => {
                const content = (
                  <>
                    <Icon className="w-5 h-5 text-burgundy-500" />
                    <span className="text-xs font-cairo font-semibold text-gray-700 text-center leading-tight">{label}</span>
                  </>
                );
                return to ? (
                  <Link key={label} to={to} className="flex flex-col items-center gap-1.5 py-1 hover:opacity-80">{content}</Link>
                ) : (
                  <div key={label} className="flex flex-col items-center gap-1.5 py-1">{content}</div>
                );
              })}
            </div>

            {/* الوصف */}
            {(product.description || product.description_ar || product.material_ar) && (
              <div className="border-t border-gray-200/80 pt-6">
                <h2 className="font-tajawal font-bold text-lg text-brand-black mb-3">وصف المنتج</h2>
                {(product.description || product.description_ar) && (
                  <p className="text-gray-700 font-cairo leading-loose whitespace-pre-line">{product.description || product.description_ar}</p>
                )}
                {product.material_ar && (
                  <p className="text-gray-700 font-cairo mt-3">
                    <span className="font-semibold">الخامة: </span>{product.material_ar}
                  </p>
                )}
              </div>
            )}

            {/* المشاركة والاستفسار */}
            <div className="border-t border-gray-200/80 pt-6 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-cairo font-semibold text-gray-700 ml-1">شاركي المنتج:</span>
                <a
                  data-testid="share-whatsapp-btn"
                  href={`https://wa.me/?text=${encodeURIComponent(whatsappShareText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="مشاركة على واتس آب"
                  aria-label="مشاركة على واتس آب"
                  className="w-10 h-10 rounded-full flex items-center justify-center bg-green-500 text-white hover:bg-green-600 transition-all hover:scale-110"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                </a>
                <button
                  data-testid="share-native-btn"
                  type="button"
                  onClick={handleShare}
                  title="مشاركة"
                  aria-label="مشاركة"
                  className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all hover:scale-110"
                >
                  <Share2 className="w-4 h-4" />
                </button>
                <button
                  data-testid="copy-link-btn"
                  type="button"
                  onClick={handleCopyLink}
                  title="نسخ الرابط"
                  aria-label="نسخ الرابط"
                  className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all hover:scale-110"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  data-testid="download-product-image-btn"
                  type="button"
                  onClick={handleDownloadImage}
                  title="تنزيل صورة المنتج"
                  aria-label="تنزيل صورة المنتج"
                  className="w-10 h-10 rounded-full flex items-center justify-center bg-burgundy-500/10 text-burgundy-500 hover:bg-burgundy-500 hover:text-white transition-all hover:scale-110"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>

              <a
                data-testid={PRODUCT_DETAIL.whatsappBtn}
                href={`${site.whatsapp_url}?text=${encodeURIComponent(whatsappInquiryText)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full h-12 rounded-full border-2 border-green-500 text-green-700 hover:bg-green-500 hover:text-white font-cairo font-bold transition-colors"
              >
                <WhatsAppIcon className="w-5 h-5" />
                استفسري عبر واتساب
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* آراء العملاء (بعد استلام المنتج + موافقة الإدارة) */}
      <ProductReviews productId={Number(id)} />

      {/* منتجات مشابهة: للمنتج ده أولاً + آخر اللي اتشاف */}
      <RecommendedSection
        ids={[Number(id), ...getRecentlyViewed().filter((x) => x !== Number(id))].slice(0, 6)}
        title="منتجات مشابهة"
        eyebrow="قد يعجبكِ أيضاً"
        showBasedOn={false}
        className="border-t border-gray-100 mt-8"
      />
    </div>
  );
};

export default ProductDetail;
